import dayjs from 'dayjs';
import { useState, useEffect, useCallback } from 'react';

import { getDuration } from 'utils';

import { Button } from '../button';

interface ResendButtonProps {
  entity?: string;
  disabled?: boolean;
  className?: string;
  cooldownTime?: number;
  startOnMount?: boolean;
  onResend: () => Promise<void> | void;
}

export const ResendButton = ({
  onResend,
  className,
  startOnMount,
  entity = 'code',
  disabled = false,
  cooldownTime = 60,
}: ResendButtonProps) => {
  const [finishedAt, setFinishedAt] = useState<number>();
  const [timeLeft, setTimeLeft] = useState(0);

  const startCooldown = useCallback(() => {
    const finish = dayjs().add(cooldownTime, 'second').valueOf();
    setFinishedAt(finish);
  }, [cooldownTime]);

  const tick = useCallback(() => {
    if (!finishedAt) {
      setTimeLeft(0);
      return;
    }

    const secondsLeft = getDuration(dayjs(), 'second', finishedAt);
    setTimeLeft(secondsLeft);
  }, [finishedAt]);

  const handleClick = useCallback(async () => {
    if (timeLeft > 0 || disabled) return;

    try {
      await onResend();
      startCooldown();
    } catch (error) {
      console.error(error);
    }
  }, [timeLeft, disabled, startCooldown, onResend]);

  useEffect(() => {
    if (startOnMount) {
      startCooldown();
    }
  }, [startOnMount, startCooldown]);

  useEffect(() => {
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [finishedAt, tick]);

  const isDisabled = timeLeft > 0 || disabled;
  const buttonText =
    timeLeft > 0 ? `Re-send ${entity} in ${timeLeft}s` : `Re-send ${entity}`;

  return (
    <Button
      variant="text"
      onClick={handleClick}
      disabled={isDisabled}
      buttonClassName={className}
    >
      {buttonText}
    </Button>
  );
};
